import { useQuery, useSuspenseQuery } from "@apollo/client";
import {
  Card,
  CardContent,
  PrimaryFullScreenSpinner,
  Skeleton,
  Txt,
} from "@repo/ui";
import { YieldPerformanceV2 } from "@repo/ui/blocks";
import { createFileRoute, Link } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { Suspense, useEffect, useState } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

const yieldSince = DateTime.now().minus({ days: 30 }).toJSDate();
// Whole calendar month, not a rolling 30 days: "July yield" has to mean July.
const lastMonth = DateTime.now().minus({ months: 1 });
const lastMonthStart = lastMonth.startOf("month").toJSDate();
const lastMonthEnd = lastMonth.endOf("month").toJSDate();

// Both cards are expensive per org (balance/yield timeseries walks over every
// asset), so they're fetched together, one org at a time, as each row scrolls
// into view rather than eagerly for the whole list.
const EAGER_ORG_COUNT = 3;

export const Route = createFileRoute(
  "/__auth/organisations_/balance-summaries",
)({
  head: () => opsHead("Balance Summaries"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { data } = useSuspenseQuery(
    graphql(`
      query OrgBalanceSummaries {
        organisations {
          id
          name
          estimatedValueIncludingInflight {
            amount
            currency
          }
        }
      }
    `),
  );

  const orgs = (data.organisations ?? [])
    .slice()
    .sort((a, b) =>
      (b.estimatedValueIncludingInflight?.amount ?? new Decimal(0))
        .minus(a.estimatedValueIncludingInflight?.amount ?? new Decimal(0))
        .toNumber(),
    );

  return (
    <AdminPageContainer>
      <AdminPageHeader title="Balance Summaries" />
      <div className="flex flex-col gap-8 p-6">
        {orgs.map((org, index) => (
          <div key={org.id} className="flex flex-col gap-2">
            <Link
              to="/organisations/$organisationId"
              params={{ organisationId: org.id }}
            >
              <Txt variant="hero-title">{org.name}</Txt>
            </Link>
            <LazyOrganisationSummary
              organisationId={org.id}
              eager={index < EAGER_ORG_COUNT}
            />
          </div>
        ))}
      </div>
    </AdminPageContainer>
  );
}

function LazyOrganisationSummary({
  organisationId,
  eager,
}: {
  organisationId: string;
  eager: boolean;
}) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(eager);

  useEffect(() => {
    if (visible || !container) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [container, visible]);

  const { data } = useQuery(
    graphql(`
      query OrgBalanceSummary(
        $organisationId: ID!
        $yieldSince: DateTime!
        $lastMonthStart: DateTime!
        $lastMonthEnd: DateTime!
      ) {
        organisation(id: $organisationId) {
          id
          ...YieldPerformanceV2
        }
      }
    `),
    {
      variables: {
        organisationId,
        yieldSince,
        lastMonthStart,
        lastMonthEnd,
      },
      skip: !visible,
    },
  );

  return (
    <div ref={setContainer} className="flex flex-col gap-4">
      {data?.organisation ? (
        <>
          <Card>
            <CardContent>
              <Txt tone="tertiary" variant="body">
                Balance breakdown coming soon
              </Txt>
            </CardContent>
          </Card>
          <YieldPerformanceV2 organisation={data.organisation} />
        </>
      ) : (
        <>
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-72 w-[550px]" />
        </>
      )}
    </div>
  );
}
